// Frontend scraper - lightweight client-side scraping using iframe
// Only runs when backend scraping returns incomplete data

/**
 * Extract price from DOM using multiple strategies
 */
const extractPrice = (doc) => {
  try {
    // 1) Try JSON-LD structured data
    const ld = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))
      .map(s => {
        try {
          return JSON.parse(s.textContent);
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);

    for (const node of ld) {
      const stack = Array.isArray(node) ? node.slice() : [node];
      while (stack.length) {
        const item = stack.pop();
        if (!item || typeof item !== 'object') continue;
        
        if (item.offers && (item.offers.price || (item.offers[0] && item.offers[0].price))) {
          const p = item.offers.price || (item.offers[0] && item.offers[0].price);
          if (p) {
            console.log('Found price via JSON-LD:', p);
            return String(p);
          }
        }
        if (item.price) {
          console.log('Found price via JSON-LD:', item.price);
          return String(item.price);
        }
        Object.values(item).forEach(v => {
          if (typeof v === 'object') stack.push(v);
        });
      }
    }

    // 2) Try meta tags and common attributes
    const selectors = [
      'meta[itemprop="price"]',
      '[itemprop="price"]',
      'meta[name="price"]',
      'meta[property*="price"]',
      '[data-price]',
      '[data-product-price]',
      '.price',
      '.product-price',
      '.current-price',
      '.price-value',
      '.main-price',
      '[data-testid*="price"]'
    ];

    for (const s of selectors) {
      const el = doc.querySelector(s);
      if (!el) continue;
      
      const val = el.content || 
                  el.getAttribute('content') || 
                  el.textContent || 
                  el.getAttribute('data-price') || 
                  el.getAttribute('data-product-price');
      
      if (val && /\d/.test(val)) {
        console.log(`Found price via selector "${s}":`, val.trim());
        return val.trim();
      }
    }

    // 3) Try to parse numbers that look like prices from visible text
    const text = doc.body.innerText || '';
    const priceRegex = /(?:€|\$|£|kr|NOK|EUR|USD|GBP)?\s?([0-9]{1,3}(?:[ ,.\u00A0][0-9]{3})*(?:[.,][0-9]{1,2})?)(?:\s?(?:€|\$|£|kr|NOK|EUR|USD|GBP))?/g;
    const matches = [];
    let m;
    while ((m = priceRegex.exec(text)) !== null) {
      if (matches.length >= 10) break;
      matches.push(m[0].trim());
    }
    
    if (matches.length > 0) {
      console.log('Found price via text regex:', matches[0]);
      return matches[0];
    }

    return null;
  } catch (error) {
    console.error('Error extracting price:', error);
    return null;
  }
};

/**
 * Extract image URL from DOM using multiple strategies
 */
const extractImage = (doc) => {
  try {
    // 1) Try meta tags
    const metaImage = doc.querySelector('meta[property="og:image"]') || 
                     doc.querySelector('meta[property="twitter:image"]') ||
                     doc.querySelector('meta[name="image"]');
    
    if (metaImage) {
      const src = metaImage.getAttribute('content');
      if (src) {
        console.log('Found image via meta tag:', src);
        return src;
      }
    }

    // 2) Try common product image selectors
    const imageSelectors = [
      'img[itemprop="image"]',
      'img.product-image',
      'img.main-image',
      '.product-gallery img',
      '.product-image img',
      '[data-image]',
      '[data-product-image]',
      'img[alt*="product"]',
      'img.main-product-image'
    ];

    for (const s of imageSelectors) {
      const img = doc.querySelector(s);
      if (img) {
        const src = img.getAttribute('src') || 
                   img.getAttribute('data-src') || 
                   img.getAttribute('data-lazy-src') ||
                   img.getAttribute('data-original');
        if (src && src !== '') {
          console.log(`Found image via selector "${s}":`, src);
          return src;
        }
      }
    }

    // 3) Try first image in body that looks like a product
    const allImages = doc.querySelectorAll('img');
    for (const img of allImages) {
      const src = img.getAttribute('src') || 
                 img.getAttribute('data-src');
      const alt = img.getAttribute('alt') || '';
      
      if (src && !src.includes('placeholder') && !src.includes('logo') && 
          (src.includes('product') || alt.toLowerCase().includes('product'))) {
        console.log('Found image via general search:', src);
        return src;
      }
    }

    return null;
  } catch (error) {
    console.error('Error extracting image:', error);
    return null;
  }
};

/**
 * Scrape product data from URL using browser iframe
 * Returns price and/or image that was missing from backend scraping
 */
export const scrapeInBrowser = async (url) => {
  return new Promise((resolve, reject) => {
    console.log('Starting frontend scraping for:', url);
    
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.cssText = 'position: absolute; width: 1px; height: 1px; left: -9999px; opacity: 0;';
    document.body.appendChild(iframe);

    const timeout = setTimeout(() => {
      console.log('Frontend scraping timeout');
      cleanup();
      resolve({ price: null, image: null });
    }, 5000); // 5 second timeout (reduced from 8)

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch (e) {
        // Ignore cleanup errors
      }
      clearTimeout(timeout);
    };

    iframe.onload = () => {
      // Check for CORS immediately
      setTimeout(() => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          
          if (!doc || !doc.body) {
            console.log('Cannot access iframe content (CORS restriction)');
            cleanup();
            resolve({ price: null, image: null });
            return;
          }

          // Extract data
          const price = extractPrice(doc);
          const image = extractImage(doc);

          console.log('Frontend scraping results:', { price, image });
          cleanup();
          resolve({ price, image });
        } catch (error) {
          console.log('CORS error accessing iframe:', error);
          cleanup();
          resolve({ price: null, image: null });
        }
      }, 2000); // Wait 2 seconds for JS to render (reduced from 3)
    };

    iframe.onerror = () => {
      console.log('Failed to load iframe');
      cleanup();
      resolve({ price: null, image: null });
    };
  });
};
